/*
 * franzi-commerce.js
 * ------------------------------------------------------------------
 * Storefront commerce behaviours for the Franzi e-shop, ported from the
 * Astro reference (public/script.js) and adapted to live Shopify data.
 *
 *   - E-shop landing: facets drawer, "Scopri altri" colour reveal, and native
 *     Search & Discovery sort form submission.
 *   - PDP: "Personalizza" toggle, inline colour swatches, the drawer-overlay
 *     selector system, and material-drawer -> sibling-nav glue.
 *
 * The mobile gallery carousel is handled by the shared [data-carousel]
 * primitive (franzi-theme.js), not here. Enqueued after swiper.umd.min.js.
 * ------------------------------------------------------------------
 */
(function () {
  "use strict";

  function readFranziI18n() {
    const node = document.querySelector("[data-franzi-i18n]");
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch {
      return {};
    }
  }
  const franziI18n = readFranziI18n();

  /* ===================================================================
   * E-SHOP LANDING
   * =================================================================== */

  function initEshopFilterDrawer() {
    const filterDrawer = document.querySelector("[data-filter-drawer]");
    const filterOpenButtons = Array.from(
      document.querySelectorAll("[data-filter-open]"),
    );
    const filterCloseButtons = Array.from(
      document.querySelectorAll("[data-filter-close]"),
    );

    if (!filterDrawer || !filterOpenButtons.length) return;

    const filterColorSection = filterDrawer.querySelector(
      "[data-filter-color-section]",
    );
    const filterColorMoreButton = filterDrawer.querySelector(
      "[data-filter-color-more]",
    );

    const setFilterColorExpanded = (isExpanded) => {
      if (!filterColorSection || !filterColorMoreButton) return;
      filterColorSection.classList.toggle("is-expanded", isExpanded);
      filterColorMoreButton.hidden = isExpanded;
      filterColorMoreButton.setAttribute("aria-expanded", String(isExpanded));
    };

    const setFilterDrawerState = (isOpen) => {
      setFilterColorExpanded(false);
      filterDrawer.hidden = !isOpen;
      filterDrawer.classList.toggle("is-open", isOpen);
      document.body.classList.toggle("has-open-filter-drawer", isOpen);
    };

    const closeFilterDrawer = () => {
      setFilterDrawerState(false);
      filterOpenButtons[0]?.focus();
    };

    filterOpenButtons.forEach((button) => {
      button.addEventListener("click", () => setFilterDrawerState(true));
    });
    filterCloseButtons.forEach((button) => {
      button.addEventListener("click", () => closeFilterDrawer());
    });
    filterColorMoreButton?.addEventListener("click", () => {
      setFilterColorExpanded(true);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || filterDrawer.hidden) return;
      event.preventDefault();
      closeFilterDrawer();
    });
    document.addEventListener("franzi:close-filter-drawer", closeFilterDrawer);
  }

  function initEshopNativeControls() {
    document.querySelectorAll("[data-native-sort-select]").forEach((select) => {
      select.addEventListener("change", () => {
        const form = select.closest("form");
        if (form) form.requestSubmit();
      });
    });

    document.querySelectorAll("[data-native-nav-select]").forEach((select) => {
      select.addEventListener("change", () => {
        if (select.value) window.location.assign(select.value);
      });
    });
  }

  /* ===================================================================
   * PDP
   * =================================================================== */

  function activateMediaSet(variantValue) {
    document.querySelectorAll("[data-franzi-media-set]").forEach((set) => {
      set.hidden = set.dataset.franziMediaSet !== variantValue;
    });
    refreshMediaSetCarousels(variantValue);
  }

  function refreshMediaSetCarousels(variantValue) {
    document
      .querySelectorAll(".product-gallery-mobile [data-carousel]")
      .forEach((viewport) => {
        const carousel = viewport.swiperApi || viewport.carouselApi;
        const track = viewport.querySelector(".carousel__track");
        if (!carousel || !track) return;

        const slides = Array.from(track.children).filter(
          (slide) =>
            slide instanceof HTMLElement &&
            slide.dataset.carouselClone !== "true",
        );
        const hasVisibleSlides = slides.some((slide) => !slide.hidden);

        window.requestAnimationFrame(() => {
          carousel.update();
          if (hasVisibleSlides) {
            // Swiper's grid only holds visible slides after update(), so the
            // new set's first frame is always index 0 — indexing over all
            // track children overshoots the grid and clamps to the last frame.
            carousel.slideTo(0, 0, false);
          }
          carousel.updateProgress(carousel.getTranslate());
          carousel.updateActiveIndex();
          carousel.updateSlidesClasses();
        });
      });
  }

  function selectPdpTab(tabId) {
    const tabs = Array.from(document.querySelectorAll("[data-pdp-tab]"));
    const panels = Array.from(
      document.querySelectorAll("[data-pdp-tab-panel]"),
    );
    if (!tabs.length) return;

    const activeTab =
      tabs.find((tab) => tab.dataset.pdpTab === tabId && !tab.hidden) ||
      tabs.find((tab) => !tab.hidden);
    const activeId = activeTab?.dataset.pdpTab || "";

    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle("is-active", isActive);
      tab.setAttribute("aria-selected", String(isActive));
    });
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.pdpTabPanel !== activeId;
    });
  }

  function activateVariantDetails(root, variantId) {
    const detailsTab = root.querySelector("[data-franzi-details-tab]");
    const detailsPanel = root.querySelector("[data-franzi-details-panel]");
    if (!detailsTab || !detailsPanel) return;

    const variantPanels = Array.from(
      detailsPanel.querySelectorAll("[data-franzi-variant-details]"),
    );
    const selectedVariantPanel = variantPanels.find(
      (panel) => panel.dataset.franziVariantDetails === variantId,
    );

    variantPanels.forEach((panel) => {
      panel.hidden = panel !== selectedVariantPanel;
    });

    const productDetails = detailsPanel.querySelector(
      "[data-franzi-product-details]",
    );
    if (productDetails) {
      productDetails.hidden = Boolean(selectedVariantPanel);
    }

    const hasDetails = Boolean(selectedVariantPanel || productDetails);
    detailsTab.hidden = !hasDetails;

    if (!hasDetails) {
      detailsPanel.hidden = true;
      if (detailsTab.classList.contains("is-active")) {
        selectPdpTab("");
      }
      return;
    }

    if (!document.querySelector("[data-pdp-tab].is-active:not([hidden])")) {
      selectPdpTab(detailsTab.dataset.pdpTab || "");
    }
  }

  // "Personalizza" toggle: swaps the default buy state for the custom state.
  function initCustomizeToggle() {
    const customization = document.querySelector(
      "[data-product-customization]",
    );
    if (!customization) return;

    const defaultPanel = customization.querySelector(
      "[data-product-summary-default]",
    );
    const customPanel = customization.querySelector(
      "[data-product-summary-custom]",
    );
    const openButton = customization.querySelector(
      "[data-product-customize-open]",
    );
    const closeButton = customization.querySelector(
      "[data-product-customize-close]",
    );

    if (!defaultPanel || !customPanel || !openButton) return;

    const setState = (isOpen) => {
      defaultPanel.hidden = isOpen;
      customPanel.hidden = !isOpen;
      openButton.setAttribute("aria-expanded", String(isOpen));
    };

    openButton.addEventListener("click", () => {
      setState(true);
      closeButton?.focus();
    });
    closeButton?.addEventListener("click", () => {
      setState(false);
      openButton.focus();
    });
    document.addEventListener("keydown", (event) => {
      if (
        event.key !== "Escape" ||
        customPanel.hidden ||
        document.body.classList.contains("has-open-drawer-overlay")
      ) {
        return;
      }
      event.preventDefault();
      setState(false);
      openButton.focus();
    });
  }

  // Issue #257: parse the plain-variant-id -> counterpart-variant-id map that
  // main-product.liquid stamps on the personalization element in
  // counterpart_swap mode.
  function parseCounterpartMap(personalization) {
    const raw = personalization?.dataset.franziCounterpartVariants;
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }

  // Issue #257: in counterpart_swap mode, disable personalization for a colour
  // that has no matching personalized counterpart variant, so we never silently
  // sell the wrong colour.
  function refreshCounterpartAvailability(root) {
    const personalization = root.querySelector("[data-franzi-personalization]");
    if (!personalization) return;
    if (
      personalization.dataset.franziPersonalizationMode !== "counterpart_swap"
    ) {
      return;
    }
    const map = parseCounterpartMap(personalization);
    const variantInput = root.querySelector("[data-franzi-variant-id]");
    const openButton = root.querySelector("[data-product-customize-open]");
    const id = variantInput?.value || "";
    const available = Boolean(
      map && Object.prototype.hasOwnProperty.call(map, id),
    );
    personalization.dataset.franziCounterpartAvailable = String(available);
    if (openButton) {
      openButton.disabled = !available;
      const message =
        personalization.dataset.franziCounterpartUnavailableMessage || "";
      if (!available && message) {
        openButton.setAttribute("title", message);
      } else {
        openButton.removeAttribute("title");
      }
    }
  }

  function applyProductColorSelection(root, option) {
    const variantInput = root.querySelector("[data-franzi-variant-id]");
    if (variantInput && option.dataset.variantId) {
      variantInput.value = option.dataset.variantId;
    }
    activateMediaSet(option.dataset.mediaSet || option.dataset.variantId || "");

    const price = root.querySelector("[data-franzi-price]");
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    activateVariantDetails(root, option.dataset.variantId || "");

    const available = option.dataset.available === "true";
    root.querySelectorAll("[data-franzi-add]").forEach((button) => {
      button.disabled = !available;
      button.textContent = available
        ? button.dataset.availableText
        : button.dataset.soldOutText;
    });

    if (option.dataset.variantUrl) {
      window.history.replaceState(null, "", option.dataset.variantUrl);
    }

    // Issue #257: keep personalization availability in sync with the colour.
    refreshCounterpartAvailability(root);
  }

  function applyCustomizationColorSelection(root, option) {
    const personalization = root.querySelector("[data-franzi-personalization]");
    if (!personalization) return;
    const colorInput = personalization.querySelector(
      "[data-franzi-personalization-color]",
    );
    if (colorInput && option.dataset.colorValue) {
      colorInput.value = option.dataset.colorValue;
    }
    if (option.dataset.franziAddonVariantId) {
      personalization.dataset.franziAddonVariantId =
        option.dataset.franziAddonVariantId;
    }
  }

  function initInlineProductColorOptions() {
    const root = document.querySelector("[data-franzi-product]");
    if (!root) return;

    const options = Array.from(
      root.querySelectorAll("[data-franzi-product-color-option]"),
    );
    if (!options.length) return;

    const setCurrentLabel = (option) => {
      const label =
        option.dataset.colorLabel ||
        option.querySelector(".sr-only")?.textContent?.trim() ||
        "";
      if (!label) return;
      root
        .querySelectorAll("[data-franzi-product-color-current]")
        .forEach((current) => {
          current.textContent = label;
        });
    };

    options.forEach((option) => {
      option.addEventListener("click", () => {
        options.forEach((candidate) => {
          const isSelected = candidate === option;
          candidate.classList.toggle("is-selected", isSelected);
          candidate.setAttribute("aria-pressed", String(isSelected));
        });

        setCurrentLabel(option);
        applyProductColorSelection(root, option);
      });
    });
  }

  // Apply the commerce side-effect of selecting a drawer option:
  //  - customization-color -> set the personalization colour property + add-on.
  function applyDrawerSelection(panelId, option) {
    const root = document.querySelector("[data-franzi-product]");
    if (!root) return;

    if (panelId === "product-color") {
      applyProductColorSelection(root, option);
    } else if (panelId === "customization-color") {
      applyCustomizationColorSelection(root, option);
    }
  }

  // The drawer-overlay selector system (material / customization-color),
  // ported from the Astro reference + the commerce glue.
  function initDrawerOverlay() {
    const overlay = document.querySelector("[data-drawer-overlay]");
    const openButtons = Array.from(
      document.querySelectorAll("[data-drawer-overlay-open]"),
    );
    const closeButtons = Array.from(
      document.querySelectorAll("[data-drawer-overlay-close]"),
    );
    const panels = Array.from(
      document.querySelectorAll("[data-drawer-overlay-panel]"),
    );

    if (!overlay || !openButtons.length || !panels.length) return;

    let activePanel = null;
    let activeTrigger = null;

    const getFocusable = (container) =>
      Array.from(
        container.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hidden && el.getAttribute("aria-hidden") !== "true");

    const setOpen = (panelId, trigger = null) => {
      const isOpen = Boolean(panelId);
      overlay.hidden = !isOpen;
      overlay.classList.toggle("is-open", isOpen);
      document.body.classList.toggle("has-open-drawer-overlay", isOpen);

      panels.forEach((panel) => {
        const isActive = isOpen && panel.dataset.drawerOverlayPanel === panelId;
        panel.hidden = !isActive;
        panel.inert = !isActive;
        panel.setAttribute("aria-hidden", String(!isActive));
        if (isActive) activePanel = panel;
      });

      openButtons.forEach((button) => {
        const controls = button.dataset.drawerOverlayOpen === panelId && isOpen;
        button.setAttribute("aria-expanded", String(controls));
      });

      if (!isOpen) {
        const focusTarget = activeTrigger;
        activePanel = null;
        activeTrigger = null;
        focusTarget?.focus();
        return;
      }

      activeTrigger = trigger;
      const closeButton = activePanel?.querySelector(
        "[data-drawer-overlay-close]",
      );
      const firstFocusable =
        closeButton || getFocusable(activePanel || overlay)[0];
      firstFocusable?.focus();
    };

    const close = () => setOpen("");

    openButtons.forEach((button) => {
      button.addEventListener("click", () =>
        setOpen(button.dataset.drawerOverlayOpen || "", button),
      );
    });
    closeButtons.forEach((button) => {
      button.addEventListener("click", () => close());
    });

    panels.forEach((panel) => {
      panel
        .querySelectorAll("[data-drawer-overlay-option]")
        .forEach((option) => {
          option.addEventListener("click", () => {
            panel
              .querySelectorAll("[data-drawer-overlay-option]")
              .forEach((button) => {
                const isSelected = button === option;
                button.classList.toggle("is-selected", isSelected);
                button.setAttribute("aria-pressed", String(isSelected));
              });

            const label = option
              .querySelector("[data-drawer-overlay-option-label]")
              ?.textContent?.trim();
            const panelId = panel.dataset.drawerOverlayPanel;

            if (label && panelId) {
              document
                .querySelectorAll(`[data-drawer-overlay-open="${panelId}"]`)
                .forEach((button) => {
                  const current = button.querySelector(
                    "[data-drawer-overlay-current]",
                  );
                  if (current) current.textContent = label;
                });
            }

            applyDrawerSelection(panelId, option);
            // Colour panels apply instantly and close; material options are
            // <a> links that navigate natively (no data-drawer-overlay-option).
            close();
          });
        });
    });

    document.addEventListener("keydown", (event) => {
      if (overlay.hidden || !activePanel) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = getFocusable(activePanel);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  // Description tabs (Descrizione / Dettagli / Spedizione e resi).
  function initPdpTabs() {
    const tabs = Array.from(document.querySelectorAll("[data-pdp-tab]"));
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        selectPdpTab(tab.dataset.pdpTab || "");
      });
    });
    selectPdpTab(
      tabs.find((tab) => tab.classList.contains("is-active"))?.dataset.pdpTab ||
        "",
    );
  }

  // Add-to-cart: native form POST for included-personalization modes; AJAX
  // /cart/add.js with a paired add-on line for bag_tag_addon / keyring modes.
  function initAddToCart() {
    const root = document.querySelector("[data-franzi-product]");
    if (!root) return;

    const form = root.querySelector("form");
    const variantInput = root.querySelector("[data-franzi-variant-id]");
    const personalization = root.querySelector("[data-franzi-personalization]");
    const formError = root.querySelector("[data-franzi-form-error]");
    if (!form || !personalization) return;

    const propertyNamePattern = /^properties\[(.+)\]$/;
    const cartDetailInput = form.querySelector(
      "[data-franzi-cart-detail-property]",
    );
    const cartDetailPropertyName =
      cartDetailInput?.name?.match(propertyNamePattern)?.[1] || "";

    const selectedAddonVariantId = () =>
      personalization.dataset.franziAddonVariantId || "";

    const personalizationProperties = () => {
      const properties = {};
      new FormData(form).forEach((value, name) => {
        const match = name.match(propertyNamePattern);
        if (!match || typeof value !== "string") return;
        const trimmed = value.trim();
        if (trimmed) properties[match[1]] = trimmed;
      });
      return properties;
    };

    const omitCartDetailProperty = (properties) =>
      Object.fromEntries(
        Object.entries(properties).filter(
          ([name]) => name !== cartDetailPropertyName,
        ),
      );

    const showError = (message) => {
      if (formError) {
        if (message) formError.textContent = message;
        formError.hidden = false;
      }
      root.querySelectorAll("[data-franzi-add]").forEach((button) => {
        button.disabled = false;
      });
    };

    // Issue #257: reflect the initial colour's personalization availability.
    refreshCounterpartAvailability(root);

    form.addEventListener("submit", async (event) => {
      const mode = personalization.dataset.franziPersonalizationMode || "";

      // Issue #257: counterpart-swap mode. When the shopper enters initials,
      // submit the personalized counterpart's same-colour variant as a single
      // cart line (personalized price). Without initials, fall through to the
      // native form POST of the plain variant (plain price).
      if (mode === "counterpart_swap") {
        const properties = personalizationProperties();
        const hasInitials = Boolean(properties.Initials);
        if (!hasInitials) return; // native submit: plain variant, plain price

        const map = parseCounterpartMap(personalization);
        const counterpartId =
          map && variantInput ? map[variantInput.value] : undefined;
        if (!counterpartId) {
          event.preventDefault();
          showError(
            personalization.dataset.franziCounterpartUnavailableMessage || "",
          );
          return;
        }

        event.preventDefault();
        if (formError) formError.hidden = true;
        root.querySelectorAll("[data-franzi-add]").forEach((button) => {
          button.disabled = true;
        });

        const quantityInput = form.querySelector('[name="quantity"]');
        const quantity = Math.max(
          1,
          Number.parseInt(quantityInput?.value || "1", 10) || 1,
        );
        const items = [
          {
            id: Number.parseInt(String(counterpartId), 10),
            quantity,
            properties,
          },
        ];

        try {
          const response = await fetch(
            root.dataset.cartAddUrl || "/cart/add.js",
            {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ items: items }),
            },
          );
          if (!response.ok) throw new Error("Cart add failed");
          window.location.href = root.dataset.cartUrl || "/cart";
        } catch (error) {
          showError();
        }
        return;
      }

      if (!["bag_tag_addon", "keyring_initials_addon"].includes(mode)) return;

      const properties = personalizationProperties();
      const hasInitials = Boolean(properties.Initials);
      const hasTagColor = Boolean(properties["Tag color"]);
      const addonVariantId = selectedAddonVariantId();
      const shouldAddPersonalization =
        (mode === "keyring_initials_addon" && hasInitials) ||
        (mode === "bag_tag_addon" && (hasTagColor || hasInitials));

      if (!shouldAddPersonalization) return; // native submit, no add-on

      if (mode === "bag_tag_addon" && (!hasTagColor || !addonVariantId)) {
        event.preventDefault();
        const colorInput = personalization.querySelector(
          "[data-franzi-personalization-color]",
        );
        showError(colorInput?.dataset.franziAddonRequiredMessage || "");
        return;
      }

      if (!addonVariantId || !variantInput) return;

      event.preventDefault();
      if (formError) formError.hidden = true;
      root.querySelectorAll("[data-franzi-add]").forEach((button) => {
        button.disabled = true;
      });

      const quantityInput = form.querySelector('[name="quantity"]');
      const quantity = Math.max(
        1,
        Number.parseInt(quantityInput?.value || "1", 10) || 1,
      );
      const group = `personalization-${variantInput.value}-${counterToken()}`;
      const mainProperties = {
        ...properties,
        _franzi_personalization_group: group,
        _franzi_personalization_mode: mode,
      };
      const addonProperties = {
        ...omitCartDetailProperty(properties),
        _franzi_personalization_addon: "true",
        _franzi_personalization_group: group,
        _franzi_parent_variant_id: variantInput.value,
        _franzi_personalization_mode: mode,
      };
      const items = [
        {
          id: Number.parseInt(variantInput.value, 10),
          quantity,
          properties: mainProperties,
        },
        {
          id: Number.parseInt(addonVariantId, 10),
          quantity,
          properties: addonProperties,
        },
      ];

      try {
        const response = await fetch(
          root.dataset.cartAddUrl || "/cart/add.js",
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ items: items }),
          },
        );
        if (!response.ok) throw new Error("Cart add failed");
        window.location.href = root.dataset.cartUrl || "/cart";
      } catch (error) {
        showError();
      }
    });
  }

  // Unique-ish token without Date.now()/Math.random reliance for grouping.
  let counter = 0;
  function counterToken() {
    counter += 1;
    return `${counter}-${performance.now().toString(16).replace(".", "")}`;
  }

  function initPdp() {
    if (!document.querySelector("[data-franzi-product]")) return;
    initCustomizeToggle();
    initInlineProductColorOptions();
    initDrawerOverlay();
    initPdpTabs();
    initAddToCart();
  }

  /* ===================================================================
   * CART
   * =================================================================== */

  function initCartAutoUpdate() {
    document.querySelectorAll("[data-franzi-cart-form]").forEach((form) => {
      const quantityInputs = Array.from(
        form.querySelectorAll("[data-franzi-cart-quantity]"),
      );
      if (!quantityInputs.length) return;

      let submitTimer = 0;

      const clearScheduledSubmit = () => {
        if (!submitTimer) return;
        window.clearTimeout(submitTimer);
        submitTimer = 0;
      };

      const quantitiesAreSubmittable = () =>
        quantityInputs.every(
          (input) => input.value !== "" && input.validity.valid,
        );

      const quantitiesHaveChanged = () =>
        quantityInputs.some(
          (input) => input.value !== input.dataset.franziCartInitialQuantity,
        );

      const submitCartUpdate = () => {
        clearScheduledSubmit();
        if (!quantitiesAreSubmittable() || !quantitiesHaveChanged()) return;
        form.requestSubmit();
      };

      const scheduleCartUpdate = () => {
        clearScheduledSubmit();
        if (!quantitiesAreSubmittable() || !quantitiesHaveChanged()) return;
        submitTimer = window.setTimeout(submitCartUpdate, 650);
      };

      quantityInputs.forEach((input) => {
        input.dataset.franziCartInitialQuantity = input.value;
        input.addEventListener("input", scheduleCartUpdate);
        input.addEventListener("change", scheduleCartUpdate);
        input.addEventListener("keydown", (event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          submitCartUpdate();
        });
      });

      form.addEventListener("submit", clearScheduledSubmit);
    });
  }

  /* ===================================================================
   * PRODUCT REGISTRATION SHOPIFY FORM
   * =================================================================== */

  const registrationFormSelector =
    '[data-forms-id="forms-root-1044547"] shopify-forms-embed';
  const registrationFormShadowStyleId = "franzi-registration-form-style";
  const registrationFormFieldMap = [
    [
      "first_name",
      "first-name",
      franziI18n.registration?.first_name ?? "Nome*",
    ],
    [
      "last_name",
      "last-name",
      franziI18n.registration?.last_name ?? "Cognome*",
    ],
    ["custom#citta", "city", franziI18n.registration?.city ?? "Città"],
    ["custom#paese", "country", franziI18n.registration?.country ?? "Paese"],
    ["email", "email", franziI18n.registration?.email ?? "E-mail*"],
    [
      "custom#codice_prodotto",
      "product-code",
      franziI18n.registration?.product_code ?? "Codice prodotto*",
    ],
  ];
  const registrationFormFieldIds = new Set(
    registrationFormFieldMap.map(([id]) => id),
  );
  const registrationFormObservers = new WeakMap();
  const registrationFormShadowCss = `
    :host {
      display: block;
      width: 100%;
    }

    #form-container-ref,
    #form-container-ref > section {
      width: 100%;
      height: auto;
    }

    #form-container-ref section[role="dialog"] {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      margin: 0;
      overflow: visible;
    }

    #form-container-ref [data-franzi-registration-content] {
      width: 100%;
      padding: 0;
    }

    #form-container-ref [data-franzi-registration-header] {
      margin: 0 0 2.625rem;
      text-align: left;
    }

    #form-container-ref [data-franzi-registration-header] h2 {
      display: none !important;
    }

    #form-container-ref [data-franzi-registration-header] :is(div, p),
    #form-container-ref [data-franzi-registration-disclaimer] {
      max-width: none;
      margin: 0;
      color: var(--body-text-color);
      font: var(--forms-body-font);
      text-align: left;
    }

    #form-container-ref [data-franzi-registration-form] {
      display: flex !important;
      flex-flow: row wrap;
      gap: var(--franzi-registration-row-gap, 0.75rem);
      width: 100%;
      margin: 0 !important;
      padding: 0 !important;
    }

    #form-container-ref [data-franzi-registration-capture] {
      display: none !important;
    }

    #form-container-ref [data-franzi-registration-field],
    #form-container-ref [data-franzi-registration-date-part] {
      position: relative;
      display: grid;
      min-width: 0;
      min-height: var(--franzi-registration-field-height, 3.1875rem);
      background: var(--form-background-color);
      text-align: left;
    }

    #form-container-ref [data-franzi-registration-field="preferred-title"] {
      flex: 0 0 var(--franzi-registration-title-width, 10ch);
    }

    #form-container-ref [data-franzi-registration-field="first-name"],
    #form-container-ref [data-franzi-registration-field="last-name"] {
      flex: 1 1 calc((100% - var(--franzi-registration-row-gap, 0.75rem)) / 2);
    }

    #form-container-ref [data-franzi-registration-form][data-franzi-registration-has-preferred-title]
      :is(
        [data-franzi-registration-field="first-name"],
        [data-franzi-registration-field="last-name"]
      ) {
      flex: 1 1 calc(
        (100% - var(--franzi-registration-title-width, 10ch) - (var(--franzi-registration-row-gap, 0.75rem) * 2)) / 2
      );
    }

    #form-container-ref [data-franzi-registration-field="city"],
    #form-container-ref [data-franzi-registration-field="country"] {
      flex: 1 1 calc((100% - var(--franzi-registration-row-gap, 0.75rem)) / 2);
    }

    #form-container-ref [data-franzi-registration-field="email"],
    #form-container-ref [data-franzi-registration-field="product-code"],
    #form-container-ref [data-franzi-registration-disclaimer],
    #form-container-ref [data-franzi-registration-submit] {
      flex: 0 0 100%;
    }

    #form-container-ref [data-franzi-registration-birthday] {
      flex: 0 0 100%;
      width: 100%;
    }

    #form-container-ref [data-franzi-registration-birthday] [class*="_birthdayText"] {
      display: block;
      margin: 0 0 1em;
      padding-bottom: 0.125rem;
      color: var(--form-text-color);
      font: normal var(--font-weight-light) 0.625rem / 1 var(--font-body);
      letter-spacing: 0.05em;
      text-align: left;
      text-transform: uppercase;
    }

    #form-container-ref [data-franzi-registration-birthday] [class*="_dateFieldContainer"] {
      display: flex;
      gap: var(--franzi-registration-row-gap, 0.75rem);
      width: 100%;
    }

    #form-container-ref [data-franzi-registration-field] input,
    #form-container-ref [data-franzi-registration-date-part] input,
    #form-container-ref [data-franzi-registration-field="preferred-title"] button {
      box-sizing: border-box;
      display: block;
      grid-area: 1 / 1;
      width: 100%;
      height: var(--franzi-registration-field-height, 3.1875rem);
      min-height: var(--franzi-registration-field-height, 3.1875rem);
      margin: 0;
      border: 0;
      border-radius: 0;
      padding: 0 var(--form-field-padding);
      background-color: transparent;
      box-shadow: none;
      color: var(--form-text-color);
      font: var(--forms-body-font);
    }

    #form-container-ref [data-franzi-registration-field="preferred-title"] button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-inline: var(--form-field-padding);
      text-align: left;
    }

    #form-container-ref [data-franzi-registration-field="preferred-title"] [class*="_selectToggleText"] {
      color: var(--form-text-color);
      font: var(--forms-body-font);
    }

    #form-container-ref [data-franzi-registration-field="preferred-title"] [class*="_arrowDownIcon"] {
      display: flex;
      height: 0.5em;
      align-items: flex-end;
    }

    #form-container-ref [data-franzi-registration-date-part="day"],
    #form-container-ref [data-franzi-registration-date-part="month"] {
      flex: 1 1 0;
    }

    #form-container-ref [data-franzi-registration-date-part="year"] {
      flex: 2 1 0;
    }

    #form-container-ref [data-franzi-registration-field] label {
      position: relative;
      z-index: 1;
      grid-area: 1 / 1;
      align-self: center;
      max-width: none;
      margin: 0;
      padding-inline: var(--form-field-padding);
      color: var(--form-text-color);
      font: var(--forms-body-font);
      line-height: normal;
      pointer-events: none;
      transform: none !important;
    }

    #form-container-ref [data-franzi-registration-field] label,
    #form-container-ref [data-franzi-registration-field="preferred-title"] > :is(p, label),
    #form-container-ref [data-franzi-registration-date-part] label {
      position: absolute;
      top: auto;
      width: 1px;
      height: 1px;
      margin: -1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      border: 0;
      padding: 0;
      padding-inline: 0;
      white-space: nowrap;
    }

    #form-container-ref [data-franzi-registration-field] input::placeholder,
    #form-container-ref [data-franzi-registration-date-part] input::placeholder {
      color: var(--form-placeholder-color);
      opacity: 1;
    }

    #form-container-ref output {
      display: block;
      flex: 0 0 100%;
      order: 999;
      width: 100%;
    }

    #form-container-ref [data-franzi-registration-disclaimer] {
      order: 60;
      margin: calc(1.625rem - var(--franzi-registration-row-gap, 0.75rem)) 0
        calc(1.5625rem - var(--franzi-registration-row-gap, 0.75rem)) !important;
      padding: 0;
      font-size: var(--franzi-registration-privacy-font-size);
      line-height: 1.25;
    }

    #form-container-ref [data-franzi-registration-disclaimer] :is(p, a) {
      margin: 0;
      color: inherit;
      font: inherit;
      text-decoration: none;
    }

    #form-container-ref [data-franzi-registration-submit] {
      order: 70;
      box-sizing: border-box;
      width: 100%;
      min-height: var(--franzi-registration-submit-height, 3.25rem);
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 0;
      padding: var(--button-padding);
      background: var(--button-background-color);
      color: var(--button-text-color);
      font: var(--forms-body-font);
      letter-spacing: 0.14em;
      line-height: normal;
      text-transform: uppercase;
    }
  `;

  function initRegistrationShopifyForms() {
    const registrationGrid = document.querySelector(".registration-grid");
    if (!registrationGrid) return;
    let registrationGridFrame = 0;

    const enhance = () => {
      document
        .querySelectorAll(registrationFormSelector)
        .forEach(queueRegistrationShopifyFormEnhance);
    };
    const scheduleEnhance = (mutations) => {
      if (
        Array.isArray(mutations) &&
        !registrationGridMutationNeedsEnhance(mutations)
      ) {
        return;
      }

      if (registrationGridFrame) return;

      registrationGridFrame = window.requestAnimationFrame(() => {
        registrationGridFrame = 0;
        enhance();
      });
    };

    enhance();

    const observer = new MutationObserver(scheduleEnhance);
    observer.observe(registrationGrid, {
      childList: true,
      subtree: true,
    });
  }

  function registrationGridMutationNeedsEnhance(mutations) {
    return mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(nodeHasRegistrationEmbed),
    );
  }

  function nodeHasRegistrationEmbed(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches(registrationFormSelector) ||
      Boolean(node.querySelector(registrationFormSelector))
    );
  }

  function queueRegistrationShopifyFormEnhance(embed) {
    if (!(embed instanceof HTMLElement)) return;
    if (embed.dataset.franziRegistrationQueued === "true") return;

    embed.dataset.franziRegistrationQueued = "true";
    window.requestAnimationFrame(() => {
      delete embed.dataset.franziRegistrationQueued;
      enhanceRegistrationShopifyForm(embed);
    });
  }

  function enhanceRegistrationShopifyForm(embed) {
    if (!(embed instanceof HTMLElement)) return;

    const root = embed.shadowRoot;
    if (!root) {
      scheduleRegistrationShopifyFormRetry(embed);
      return;
    }

    disconnectRegistrationShopifyFormObserver(embed);

    if (!root.getElementById(registrationFormShadowStyleId)) {
      const style = document.createElement("style");
      style.id = registrationFormShadowStyleId;
      style.textContent = registrationFormShadowCss;
      root.append(style);
    }

    const form = root.querySelector("form");
    if (!form) {
      scheduleRegistrationShopifyFormRetry(embed);
      observeRegistrationShopifyForm(embed, root);
      return;
    }

    delete embed.dataset.franziRegistrationAttempts;
    delete embed.dataset.franziRegistrationRetryQueued;

    const header = root.querySelector('[class*="_formHeader"]');
    const content = root.querySelector('[class*="_gridItemContent"]');
    const submit = form.querySelector('button[type="submit"]');
    const disclaimer = root.querySelector('[class*="_formDisclaimer"]');

    if (content instanceof HTMLElement) {
      setRegistrationDataFlag(content, "franziRegistrationContent");
    }

    if (header instanceof HTMLElement) {
      setRegistrationDataFlag(header, "franziRegistrationHeader");
    }

    setRegistrationDataFlag(form, "franziRegistrationForm");

    Array.from(form.children).forEach((child) => {
      if (
        child instanceof HTMLElement &&
        child.querySelector("shop-lead-capture")
      ) {
        setRegistrationDataFlag(child, "franziRegistrationCapture");
      }
    });

    let enhancedFieldCount = 0;
    removeRegistrationSyntheticSalutation(root);
    const preferredTitle = enhanceRegistrationPreferredTitle(root, form);
    enhanceRegistrationBirthday(root, form);
    if (preferredTitle) {
      setRegistrationDataFlag(form, "franziRegistrationHasPreferredTitle");
    } else {
      delete form.dataset.franziRegistrationHasPreferredTitle;
    }

    registrationFormFieldMap.forEach(([id, key, label]) => {
      const field = enhanceRegistrationField(root, id, key, label);
      if (field) {
        enhancedFieldCount += 1;
      }
    });

    if (submit instanceof HTMLElement) {
      setRegistrationDataFlag(submit, "franziRegistrationSubmit");
    }

    if (disclaimer instanceof HTMLElement && submit instanceof HTMLElement) {
      setRegistrationDataFlag(disclaimer, "franziRegistrationDisclaimer");
      if (disclaimer.nextElementSibling !== submit) {
        form.insertBefore(disclaimer, submit);
      }
    }

    if (enhancedFieldCount < registrationFormFieldMap.length) {
      scheduleRegistrationShopifyFormRetry(embed);
    }

    observeRegistrationShopifyForm(embed, root);
  }

  function removeRegistrationSyntheticSalutation(root) {
    root.getElementById("franzi-registration-salutation")?.remove();
  }

  function enhanceRegistrationPreferredTitle(root, form) {
    const toggle =
      root.querySelector('[aria-controls*="custom#titolo_preferito"]') ||
      findRegistrationFormChildByText(
        form,
        franziI18n.registration?.preferred_title ?? "Titolo preferito",
      )?.querySelector("button");
    const field = toggle?.closest("div");

    if (!(field instanceof HTMLElement)) return null;

    if (field.dataset.franziRegistrationField !== "preferred-title") {
      field.dataset.franziRegistrationField = "preferred-title";
    }

    if (toggle instanceof HTMLElement) {
      setRegistrationFieldAccessibleText(
        toggle,
        franziI18n.registration?.preferred_title ?? "Titolo preferito",
      );
    }

    return field;
  }

  function enhanceRegistrationBirthday(root, form) {
    const birthday =
      root.querySelector('[data-testid="date-birthday"]') ||
      findRegistrationFormChildByText(
        form,
        franziI18n.registration?.birthday ?? "Data di nascita",
      );

    if (!(birthday instanceof HTMLElement)) return null;

    setRegistrationDataFlag(birthday, "franziRegistrationBirthday");

    const dateParts = [
      [
        franziI18n.registration?.birthday_day ?? "DD",
        "day",
        franziI18n.registration?.birthday_day ?? "DD",
        franziI18n.registration?.birthday_day_aria ?? "Data di nascita DD",
      ],
      [
        franziI18n.registration?.birthday_month ?? "MM",
        "month",
        franziI18n.registration?.birthday_month ?? "MM",
        franziI18n.registration?.birthday_month_aria ?? "Data di nascita MM",
      ],
      [
        franziI18n.registration?.birthday_year ?? "YYYY",
        "year",
        franziI18n.registration?.birthday_year ?? "YYYY",
        franziI18n.registration?.birthday_year_aria ?? "Data di nascita YYYY",
      ],
    ];

    dateParts.forEach(([label, key, placeholder, ariaLabel]) => {
      const labelElement = findRegistrationLabelByText(birthday, label);
      const field = labelElement?.closest("div");
      const input = field?.querySelector("input");

      if (field instanceof HTMLElement) {
        field.dataset.franziRegistrationDatePart = key;
      }

      setRegistrationFieldAccessibleText(input, placeholder, ariaLabel);
    });

    return birthday;
  }

  function enhanceRegistrationField(root, id, key, label) {
    const input = root.getElementById(id);
    const field = input?.closest("div");
    const labelElement = field?.querySelector("label");

    if (!(field instanceof HTMLElement)) return null;

    if (field.dataset.franziRegistrationField !== key) {
      field.dataset.franziRegistrationField = key;
    }

    if (
      labelElement instanceof HTMLElement &&
      labelElement.textContent !== label
    ) {
      labelElement.textContent = label;
    }

    setRegistrationFieldAccessibleText(input, label);

    return field;
  }

  function setRegistrationFieldAccessibleText(input, placeholder, ariaLabel) {
    if (!(input instanceof HTMLElement)) return;
    const accessibleLabel = ariaLabel || placeholder;

    if ("placeholder" in input && input.placeholder !== placeholder) {
      input.placeholder = placeholder;
    }

    if (input.getAttribute("aria-label") !== accessibleLabel) {
      input.setAttribute("aria-label", accessibleLabel);
    }
  }

  function findRegistrationFormChildByText(form, text) {
    return Array.from(form.children).find((child) =>
      normalizeRegistrationText(child.textContent).includes(text),
    );
  }

  function findRegistrationLabelByText(container, text) {
    return Array.from(container.querySelectorAll("label")).find(
      (label) => normalizeRegistrationText(label.textContent) === text,
    );
  }

  function normalizeRegistrationText(value) {
    return (value || "").replace(/\s+/g, " ").trim();
  }

  function setRegistrationDataFlag(element, key) {
    if (element.dataset[key] !== "true") {
      element.dataset[key] = "true";
    }
  }

  function observeRegistrationShopifyForm(embed, root) {
    disconnectRegistrationShopifyFormObserver(embed);
    embed.dataset.franziRegistrationObserver = "true";

    const observer = new MutationObserver((mutations) => {
      if (registrationFormMutationNeedsEnhance(mutations)) {
        queueRegistrationShopifyFormEnhance(embed);
      }
    });
    registrationFormObservers.set(embed, observer);
    observer.observe(root, { childList: true, subtree: true });
  }

  function disconnectRegistrationShopifyFormObserver(embed) {
    const observer = registrationFormObservers.get(embed);
    if (!observer) return;

    observer.takeRecords();
    observer.disconnect();
  }

  function registrationFormMutationNeedsEnhance(mutations) {
    return mutations.some((mutation) =>
      [...mutation.addedNodes, ...mutation.removedNodes].some(
        nodeHasRegistrationFormStructure,
      ),
    );
  }

  function nodeHasRegistrationFormStructure(node) {
    if (!(node instanceof Element)) return false;

    return (
      elementIsRegistrationFormStructure(node) ||
      Boolean(
        node.querySelector(
          'form, button[type="submit"], [aria-controls*="custom#titolo_preferito"], [data-testid="date-birthday"], shop-lead-capture, [class*="_formHeader"], [class*="_gridItemContent"], [class*="_formDisclaimer"]',
        ),
      ) ||
      elementContainsRegistrationField(node)
    );
  }

  function elementIsRegistrationFormStructure(element) {
    return (
      element.localName === "form" ||
      element.localName === "shop-lead-capture" ||
      registrationFormFieldIds.has(element.id) ||
      element.getAttribute("data-testid") === "date-birthday" ||
      element.matches(
        'button[type="submit"], [aria-controls*="custom#titolo_preferito"], [class*="_selectContainer"], [class*="_dateFieldContainer"], [class*="_formHeader"], [class*="_gridItemContent"], [class*="_formDisclaimer"]',
      )
    );
  }

  function elementContainsRegistrationField(element) {
    if (registrationFormFieldIds.has(element.id)) return true;

    return Array.from(element.querySelectorAll("[id]")).some((child) =>
      registrationFormFieldIds.has(child.id),
    );
  }

  function scheduleRegistrationShopifyFormRetry(embed) {
    const attempts = Number.parseInt(
      embed.dataset.franziRegistrationAttempts || "0",
      10,
    );
    if (attempts >= 80) return;
    if (embed.dataset.franziRegistrationRetryQueued === "true") return;

    embed.dataset.franziRegistrationRetryQueued = "true";
    embed.dataset.franziRegistrationAttempts = String(attempts + 1);
    window.setTimeout(() => {
      delete embed.dataset.franziRegistrationRetryQueued;
      enhanceRegistrationShopifyForm(embed);
    }, 100);
  }

  /* ===================================================================
   * CONTACT SHOPIFY FORM
   * =================================================================== */

  const contactFormSelector =
    '[data-forms-id="forms-root-1052340"] shopify-forms-embed';
  const contactFormShadowStyleId = "franzi-contact-form-style";
  const contactFormAriaLabel =
    franziI18n.contact?.aria_label ?? "Modulo contatti";
  const contactFormIntroLines = [
    /*
    "Per qualsiasi domanda o richiesta, La invitiamo a compilare il form sottostante.",
    "Il nostro team Le risponderà nel più breve tempo possibile, fornendo un’assistenza attenta e dedicata.",
    */
  ];
  const contactFormPrivacyText =
    franziI18n.contact?.privacy ??
    "Per maggiori informazioni su come utilizziamo i tuoi dati personali, consulta la nostra Informativa sulla Privacy.";
  const contactFormSubmitLabel = franziI18n.contact?.submit ?? "Invia";
  const contactFormFieldMap = [
    {
      ids: ["first_name"],
      key: "first-name",
      label: franziI18n.contact?.first_name ?? "Nome*",
      control: "input",
    },
    {
      ids: ["last_name"],
      key: "last-name",
      label: franziI18n.contact?.last_name ?? "Cognome*",
      control: "input",
    },
    {
      ids: ["email"],
      key: "email",
      label: franziI18n.contact?.email ?? "E-mail*",
      control: "input",
    },
    {
      ids: ["phone", "custom#numero_di_telefono"],
      key: "phone",
      label: franziI18n.contact?.phone ?? "Numero di telefono*",
      control: "input",
    },
    {
      ids: [
        "body",
        "message",
        "custom#message",
        "custom#messaggio",
        "custom#scrivi_qui_il_tuo_messaggio",
      ],
      key: "message",
      label: franziI18n.contact?.message ?? "Scrivi qui il tuo messaggio",
      control: "textarea",
    },
  ];
  const contactFormFieldIds = new Set(
    contactFormFieldMap.reduce((ids, field) => ids.concat(field.ids), []),
  );
  const contactFormObservers = new WeakMap();
  const contactFormShadowCss = `
    :host {
      display: block;
      width: 100%;
    }

    #form-container-ref,
    #form-container-ref > section {
      width: 100%;
      height: auto;
    }

    #form-container-ref section[role="dialog"] {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      margin: 0;
      overflow: visible;
    }

    #form-container-ref [data-franzi-contact-content] {
      width: 100%;
      padding: 0;
    }

    #form-container-ref [data-franzi-contact-header] {
      margin: 0 0 1.875rem;
      text-align: left;
    }

    #form-container-ref [data-franzi-contact-header] h2 {
      display: none !important;
    }

    #form-container-ref [data-franzi-contact-header] :is(div, p),
    #form-container-ref [data-franzi-contact-disclaimer] {
      max-width: none;
      margin: 0;
      color: var(--body-text-color);
      font: var(--forms-body-font);
      text-align: left;
    }

    #form-container-ref [data-franzi-contact-form] {
      display: flex !important;
      flex-flow: row wrap;
      gap: var(--franzi-contact-row-gap, 0.75rem);
      width: 100%;
      margin: 0 !important;
      padding: 0 !important;
    }

    #form-container-ref [data-franzi-contact-capture] {
      display: none !important;
    }

    #form-container-ref [data-franzi-contact-field] {
      position: relative;
      display: grid;
      min-width: 0;
      min-height: var(--franzi-contact-field-height, 3.1875rem);
      background: var(--form-background-color);
      text-align: left;
    }

    #form-container-ref [data-franzi-contact-field="first-name"],
    #form-container-ref [data-franzi-contact-field="last-name"] {
      flex: 1 1 calc((100% - var(--franzi-contact-row-gap, 0.75rem)) / 2);
    }

    #form-container-ref [data-franzi-contact-field="email"],
    #form-container-ref [data-franzi-contact-field="phone"],
    #form-container-ref [data-franzi-contact-field="message"],
    #form-container-ref [data-franzi-contact-disclaimer],
    #form-container-ref [data-franzi-contact-submit] {
      flex: 0 0 100%;
    }

    #form-container-ref [data-franzi-contact-field="message"] {
      min-height: var(--franzi-contact-message-height, 7.5625rem);
    }

    #form-container-ref [data-franzi-contact-field] :is(input, textarea, select) {
      box-sizing: border-box;
      display: block;
      grid-area: 1 / 1;
      width: 100%;
      height: var(--franzi-contact-field-height, 3.1875rem);
      min-height: var(--franzi-contact-field-height, 3.1875rem);
      margin: 0;
      border: 0;
      border-radius: 0;
      padding: 0 var(--form-field-padding);
      background-color: transparent;
      box-shadow: none;
      color: var(--form-text-color);
      font: var(--forms-body-font);
    }

    #form-container-ref [data-franzi-contact-field] textarea {
      height: auto;
      min-height: var(--franzi-contact-message-height, 7.5625rem);
      padding-top: var(--form-field-padding);
      resize: none;
    }

    #form-container-ref [data-franzi-contact-field] label {
      position: absolute;
      top: auto;
      width: 1px;
      height: 1px;
      margin: -1px;
      overflow: hidden;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      border: 0;
      padding: 0;
      padding-inline: 0;
      white-space: nowrap;
    }

    #form-container-ref [class*="_formFieldContainer"]:has(textarea)::before {
      display: none !important;
    }

    #form-container-ref [class*="_selectToggle"] {
      max-height: var(--franzi-contact-field-height, 3.1875rem);
    }

    #form-container-ref [class*="_arrowDownIcon"] {
      margin: 0 !important;
    }

    #form-container-ref [data-franzi-contact-field] :is(input, textarea)::placeholder {
      color: var(--form-placeholder-color);
      opacity: 1;
    }

    #form-container-ref output {
      display: block;
      flex: 0 0 100%;
      order: 999;
      width: 100%;
    }

    #form-container-ref [data-franzi-contact-disclaimer] {
      order: 60;
      border: 0;
      margin: calc(1.875rem - var(--franzi-contact-row-gap, 0.75rem)) 0
        calc(1.875rem - var(--franzi-contact-row-gap, 0.75rem)) !important;
      padding: 0;
      font-size: var(--franzi-contact-privacy-font-size);
      line-height: 1.25;
    }

    #form-container-ref [data-franzi-contact-disclaimer] legend {
      display: none !important;
    }

    #form-container-ref [data-franzi-contact-disclaimer] label {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
    }

    #form-container-ref [data-franzi-contact-disclaimer] input {
      flex: 0 0 auto;
      margin-top: 0.15em;
    }

    #form-container-ref [data-franzi-contact-disclaimer] :is(p, a, label, span) {
      margin: 0;
      color: inherit;
      font: inherit;
      text-decoration: none;
    }

    #form-container-ref [data-franzi-contact-submit] {
      order: 70;
      box-sizing: border-box;
      width: 100%;
      min-height: var(--franzi-contact-submit-height, 2.8125rem);
      align-items: center;
      justify-content: center;
      border: 0;
      border-radius: 0;
      padding: var(--button-padding);
      background: var(--button-background-color);
      color: var(--button-text-color);
      font: var(--forms-body-font);
      letter-spacing: 0.14em;
      line-height: normal;
      text-transform: uppercase;
    }

    @media (max-width: 48rem) {
      #form-container-ref [data-franzi-contact-field="first-name"],
      #form-container-ref [data-franzi-contact-field="last-name"] {
        flex-basis: 100%;
      }
    }
  `;

  function initContactShopifyForms() {
    const contactFormSection = document.querySelector(".contact-form-section");
    if (!contactFormSection) return;
    let contactFormFrame = 0;

    const enhance = () => {
      document
        .querySelectorAll(contactFormSelector)
        .forEach(queueContactShopifyFormEnhance);
    };
    const scheduleEnhance = (mutations) => {
      if (
        Array.isArray(mutations) &&
        !contactSectionMutationNeedsEnhance(mutations)
      ) {
        return;
      }

      if (contactFormFrame) return;

      contactFormFrame = window.requestAnimationFrame(() => {
        contactFormFrame = 0;
        enhance();
      });
    };

    enhance();

    const observer = new MutationObserver(scheduleEnhance);
    observer.observe(contactFormSection, {
      childList: true,
      subtree: true,
    });
  }

  function contactSectionMutationNeedsEnhance(mutations) {
    return mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some(nodeHasContactEmbed),
    );
  }

  function nodeHasContactEmbed(node) {
    if (!(node instanceof Element)) return false;

    return (
      node.matches(contactFormSelector) ||
      Boolean(node.querySelector(contactFormSelector))
    );
  }

  function queueContactShopifyFormEnhance(embed) {
    if (!(embed instanceof HTMLElement)) return;
    if (embed.dataset.franziContactQueued === "true") return;

    embed.dataset.franziContactQueued = "true";
    window.requestAnimationFrame(() => {
      delete embed.dataset.franziContactQueued;
      enhanceContactShopifyForm(embed);
    });
  }

  function enhanceContactShopifyForm(embed) {
    if (!(embed instanceof HTMLElement)) return;

    const root = embed.shadowRoot;
    if (!root) {
      scheduleContactShopifyFormRetry(embed);
      return;
    }

    disconnectContactShopifyFormObserver(embed);

    if (!root.getElementById(contactFormShadowStyleId)) {
      const style = document.createElement("style");
      style.id = contactFormShadowStyleId;
      style.textContent = contactFormShadowCss;
      root.append(style);
    }

    const form = root.querySelector("form");
    if (!form) {
      scheduleContactShopifyFormRetry(embed);
      observeContactShopifyForm(embed, root);
      return;
    }

    delete embed.dataset.franziContactAttempts;
    delete embed.dataset.franziContactRetryQueued;

    const header = root.querySelector('[class*="_formHeader"]');
    const content = root.querySelector('[class*="_gridItemContent"]');
    const submit = form.querySelector('button[type="submit"]');
    const dialog = root.querySelector("section[aria-label]");
    const disclaimer =
      enhanceContactPrivacyField(form) ||
      root.querySelector('[class*="_formDisclaimer"]');

    if (dialog instanceof HTMLElement) {
      dialog.setAttribute("aria-label", contactFormAriaLabel);
    }

    if (content instanceof HTMLElement) {
      setContactDataFlag(content, "franziContactContent");
    }

    if (header instanceof HTMLElement) {
      setContactDataFlag(header, "franziContactHeader");
      setContactHeaderCopy(header);
    }

    setContactDataFlag(form, "franziContactForm");

    Array.from(form.children).forEach((child) => {
      if (
        child instanceof HTMLElement &&
        child.querySelector("shop-lead-capture")
      ) {
        setContactDataFlag(child, "franziContactCapture");
      }
    });

    let enhancedFieldCount = 0;
    contactFormFieldMap.forEach((fieldConfig) => {
      const field = enhanceContactField(root, form, fieldConfig);
      if (field) {
        enhancedFieldCount += 1;
      }
    });

    if (submit instanceof HTMLElement) {
      setContactDataFlag(submit, "franziContactSubmit");
      setContactSubmitLabel(submit);
    }

    if (disclaimer instanceof HTMLElement && submit instanceof HTMLElement) {
      setContactDataFlag(disclaimer, "franziContactDisclaimer");
      if (disclaimer.nextElementSibling !== submit) {
        form.insertBefore(disclaimer, submit);
      }
    }

    if (enhancedFieldCount < contactFormFieldMap.length) {
      scheduleContactShopifyFormRetry(embed);
    }

    observeContactShopifyForm(embed, root);
  }

  function enhanceContactField(root, form, fieldConfig) {
    const fieldMatch = findContactField(root, form, fieldConfig);
    const field = fieldMatch?.field;
    const input = fieldMatch?.input;

    if (!(field instanceof HTMLElement)) return null;

    if (field.dataset.franziContactField !== fieldConfig.key) {
      field.dataset.franziContactField = fieldConfig.key;
    }

    const labelElement = field.querySelector("label");
    if (
      labelElement instanceof HTMLElement &&
      normalizeContactText(labelElement.textContent) !== fieldConfig.label
    ) {
      labelElement.textContent = fieldConfig.label;
    }

    setContactFieldAccessibleText(input, fieldConfig.label);

    return field;
  }

  function setContactHeaderCopy(header) {
    const heading = header.querySelector("h2");
    const body = header.querySelector('[class*="_textBody"]');
    if (heading instanceof HTMLElement) {
      heading.textContent = contactFormAriaLabel;
    }

    if (!(body instanceof HTMLElement)) return;

    body.replaceChildren(
      ...contactFormIntroLines.flatMap((line, index) => {
        const nodes = [];
        if (index > 0) {
          nodes.push(
            document.createElement("br"),
            document.createTextNode(" "),
          );
        }
        nodes.push(document.createTextNode(line));
        return nodes;
      }),
    );
  }

  function enhanceContactPrivacyField(form) {
    const privacyControl = form.querySelector(
      `input[name="${escapeContactAttributeValue("custom#privacy")}"]`,
    );
    const privacyField = privacyControl?.closest("fieldset");
    if (!(privacyField instanceof HTMLElement)) return null;

    const label = privacyField.querySelector("label");
    const labelText =
      label?.querySelector("span") || label?.querySelector("p") || label;

    if (labelText instanceof HTMLElement) {
      labelText.textContent = contactFormPrivacyText;
    }

    if (privacyControl instanceof HTMLElement) {
      privacyControl.setAttribute("aria-label", contactFormPrivacyText);
    }

    return privacyField;
  }

  function setContactSubmitLabel(submit) {
    if (normalizeContactText(submit.textContent) !== contactFormSubmitLabel) {
      submit.textContent = contactFormSubmitLabel;
    }
  }

  function findContactField(root, form, fieldConfig) {
    for (const id of fieldConfig.ids) {
      const input = findContactInputByIdentifier(root, id);
      const field = input?.closest("div");
      if (field instanceof HTMLElement) {
        return { field, input };
      }
    }

    const field =
      findContactFormChildByText(form, fieldConfig.label) ||
      findContactFormChildByText(
        form,
        removeContactRequiredMarker(fieldConfig.label),
      );

    if (!(field instanceof HTMLElement)) return null;

    const input = field.querySelector(fieldConfig.control);
    return { field, input };
  }

  function findContactInputByIdentifier(root, identifier) {
    return (
      root.getElementById(identifier) ||
      root.querySelector(`[name="${escapeContactAttributeValue(identifier)}"]`)
    );
  }

  function escapeContactAttributeValue(value) {
    return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  function setContactFieldAccessibleText(input, placeholder, ariaLabel) {
    if (!(input instanceof HTMLElement)) return;
    const accessibleLabel = ariaLabel || placeholder;

    if ("placeholder" in input && input.placeholder !== placeholder) {
      input.placeholder = placeholder;
    }

    if (input.getAttribute("aria-label") !== accessibleLabel) {
      input.setAttribute("aria-label", accessibleLabel);
    }
  }

  function findContactFormChildByText(form, text) {
    const expected = normalizeContactText(text);
    const expectedWithoutMarker = removeContactRequiredMarker(expected);

    return Array.from(form.children).find((child) => {
      const actual = normalizeContactText(child.textContent);
      const actualWithoutMarker = removeContactRequiredMarker(actual);

      return (
        actual.includes(expected) ||
        actualWithoutMarker.includes(expectedWithoutMarker)
      );
    });
  }

  function normalizeContactText(value) {
    return (value || "").replace(/\s+/g, " ").trim();
  }

  function removeContactRequiredMarker(value) {
    return normalizeContactText(value).replace(/\*+$/, "").trim();
  }

  function setContactDataFlag(element, key) {
    if (element.dataset[key] !== "true") {
      element.dataset[key] = "true";
    }
  }

  function observeContactShopifyForm(embed, root) {
    disconnectContactShopifyFormObserver(embed);
    embed.dataset.franziContactObserver = "true";

    const observer = new MutationObserver((mutations) => {
      if (contactFormMutationNeedsEnhance(mutations)) {
        queueContactShopifyFormEnhance(embed);
      }
    });
    contactFormObservers.set(embed, observer);
    observer.observe(root, { childList: true, subtree: true });
  }

  function disconnectContactShopifyFormObserver(embed) {
    const observer = contactFormObservers.get(embed);
    if (!observer) return;

    observer.takeRecords();
    observer.disconnect();
  }

  function contactFormMutationNeedsEnhance(mutations) {
    return mutations.some((mutation) =>
      [...mutation.addedNodes, ...mutation.removedNodes].some(
        nodeHasContactFormStructure,
      ),
    );
  }

  function nodeHasContactFormStructure(node) {
    if (!(node instanceof Element)) return false;

    return (
      elementIsContactFormStructure(node) ||
      Boolean(
        node.querySelector(
          'form, button[type="submit"], shop-lead-capture, [class*="_formHeader"], [class*="_gridItemContent"], [class*="_formDisclaimer"]',
        ),
      ) ||
      elementContainsContactField(node)
    );
  }

  function elementIsContactFormStructure(element) {
    return (
      element.localName === "form" ||
      element.localName === "shop-lead-capture" ||
      contactFormFieldIds.has(element.id) ||
      element.matches(
        'button[type="submit"], [class*="_formHeader"], [class*="_gridItemContent"], [class*="_formDisclaimer"]',
      )
    );
  }

  function elementContainsContactField(element) {
    if (contactFormFieldIds.has(element.id)) return true;

    return Array.from(element.querySelectorAll("[id]")).some((child) =>
      contactFormFieldIds.has(child.id),
    );
  }

  function scheduleContactShopifyFormRetry(embed) {
    const attempts = Number.parseInt(
      embed.dataset.franziContactAttempts || "0",
      10,
    );
    if (attempts >= 80) return;
    if (embed.dataset.franziContactRetryQueued === "true") return;

    embed.dataset.franziContactRetryQueued = "true";
    embed.dataset.franziContactAttempts = String(attempts + 1);
    window.setTimeout(() => {
      delete embed.dataset.franziContactRetryQueued;
      enhanceContactShopifyForm(embed);
    }, 100);
  }

  /* ===================================================================
   * BOOT
   * =================================================================== */

  function boot() {
    initEshopFilterDrawer();
    initEshopNativeControls();
    initPdp();
    initCartAutoUpdate();
    initRegistrationShopifyForms();
    initContactShopifyForms();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
